import { useOpenDoc, type OpenDoc } from '../files/openDoc'

/**
 * A picture in a message, and what a click on it does.
 *
 * With a viewer to hand it to — an `onOpen` prop (the project chat threads one) or
 * the OpenDoc context (Clean View provides one) — the tile is a BUTTON that opens the
 * cover-panel viewer; `.msg-img.tap` strips the button chrome so it reads as the
 * image it wraps. With neither, the anchor Clean View always had: the picture in a
 * new tab.
 *
 * Three components drew this pair by hand — the note file, the bare path and the
 * project file — and the owner's 2026-09-13 ruling on where a click goes (a cover
 * screen, never a download) had to land in all three. One tile, so it cannot land
 * in two.
 */
export function ImageTile({
  url,
  name,
  mime,
  onOpen,
  onError,
}: {
  url: string
  name: string
  mime: string
  /** An explicit viewer wins over the context; absent, the context is asked. */
  onOpen?: OpenDoc
  /** The bytes came but the browser would not decode them — the parent shows a chip. */
  onError: () => void
}) {
  const fromAbove = useOpenDoc()
  const open = onOpen ?? fromAbove
  if (!open) {
    return (
      <a className="msg-img" href={url} target="_blank" rel="noreferrer">
        {/* No width/height: the transcript does not carry them, and guessing would
            reserve the wrong box and then jump when the real one lands. */}
        <img src={url} alt={name} loading="lazy" onError={onError} />
      </a>
    )
  }
  return (
    <button type="button" className="msg-img tap" title={`Open ${name}`} onClick={() => open({ url, name, mime })}>
      <img src={url} alt={name} loading="lazy" onError={onError} />
    </button>
  )
}

/**
 * The mime an image's NAME implies. Used where nothing else declares one: to type
 * the blob a note file is rebuilt into (a typeless blob is what WebKit saves rather
 * than shows) and to tell the viewer which kind of thing it was handed.
 */
export function imageMime(name: string): string {
  const ext = name.toLowerCase().split('.').pop() ?? ''
  const sub = ext === 'jpg' ? 'jpeg' : ext === 'svg' ? 'svg+xml' : ext || 'png'
  return `image/${sub}`
}
